import React from 'react';
import { Button, Glyphicon, Tab, Tabs } from 'react-bootstrap';
import { translate } from 'react-i18next';
import { RectangularInputForm } from './RectangularInputForm';
import { InputFormCard } from './InputFormCard';
import PolygonInputForm from './PolygonInputForm';

// Helper to approximate a circle with N segments (centered at cx, cy)
const generateCircleVertices = (radius, segments = 16, cx = 0, cy = 0) => {
  const r = parseFloat(radius) || 0;
  if (r <= 0) return [];
  const verts = [];
  for (let i = 0; i < segments; i++) {
    const theta = (2 * Math.PI * i) / segments;
    const x = cx + r * Math.cos(theta);
    const y = cy + r * Math.sin(theta);
    verts.push({ x, y, z: 0 });
  }
  return verts;
};

const SC_EDITOR_STORAGE_KEY = 'sc_editor_mode_cache';

/**
 * - Show/hide between tab
 * - Add, remove window form and shading device forms
 */
export class SCControlPanel extends React.Component {
  static getStorageCache() {
    try {
      const cached = localStorage.getItem(SC_EDITOR_STORAGE_KEY);
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      return {};
    }
  }

  static setStorageCache(data) {
    try {
      localStorage.setItem(SC_EDITOR_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      // ignore
    }
  }

  static clearStorageCache() {
    try {
      localStorage.removeItem(SC_EDITOR_STORAGE_KEY);
    } catch (e) {
      // ignore
    }
  }

  static convertToDevConfig(dev) {
    if (dev.vertices) {
      const result = { vertices: dev.vertices };
      if (dev.radius !== undefined) {
        result.radius = dev.radius;
      }
      if (dev.shape) {
        result.shape = dev.shape;
      }
      if (dev.xAngle !== undefined) {
        result.xAngle = dev.xAngle;
      }
      if (dev.angle !== undefined) {
        result.angle = dev.angle;
      }
      if (dev.origin) {
        result.origin = dev.origin;
      } else if (dev.x !== undefined || dev.y !== undefined || dev.z !== undefined) {
        result.origin = {
          x: dev.x || 0,
          y: dev.y || 0,
          z: dev.z || 0,
        };
      }
      if (dev.azimuth !== undefined) {
        result.azimuth = dev.azimuth;
      }
      return result;
    }
    return {
      origin: {
        x: dev.x,
        y: dev.y,
        z: dev.z,
      },
      size: {
        width: dev.width,
        height: dev.height || dev.length,
      },
      xAngle: dev.angle,
      shape: dev.shape,
      radius: dev.radius,
      azimuth: dev.azimuth,
    };
  }

  static getConfigForDevice(type, windowWidth, windowHeight) {
    const defaultDeviceConfig = {
      origin: {
        x: 0,
        y: 0,
        z: 0,
      },
      size: {
        width: windowWidth,
        height: windowHeight,
      },
      isShow: true,
      isHighLight: false,
      shape: 'rectangle',
    };

    switch (type) {
      case 'right':
        return {
          ...defaultDeviceConfig,
          origin: {
            x: 0,
            y: windowWidth,
            z: 0,
          },
        };
      case 'top':
        return {
          ...defaultDeviceConfig,
          origin: {
            x: 0,
            y: 0,
            z: windowHeight,
          },
          xAngle: 75,
        };
      case 'front':
        return {
          ...defaultDeviceConfig,
          origin: {
            x: windowHeight,
            y: 0,
            z: windowHeight,
          },
          xAngle: 10,
        };
      default:
        return defaultDeviceConfig;
    }
  }

  constructor(props) {
    super(props);

    const initialWindowWidth = props.window.width || (props.window && props.window.vertices
      ? props.window.vertices.reduce((m, p) => Math.max(m, parseFloat(p.x || 0)), 0)
      : 0);
    const initialWindowHeight = props.window.height || (props.window && props.window.vertices
      ? props.window.vertices.reduce((m, p) => Math.max(m, parseFloat(p.y || 0)), 0)
      : 0);
    const initialWindowRadius = Math.min(
      initialWindowWidth || 1,
      initialWindowHeight || 1,
    ) / 2 || 0.5;

    this.state = {
      windowMode: props.windowMode || (props.window && props.window.vertices ? 'polygon' : 'rectangle'),
      windowVertices: (props.window && props.window.vertices) || [],
      windowWidth: initialWindowWidth,
      windowHeight: initialWindowHeight,
      windowCircleRadius: initialWindowRadius,
      leftDevices: props.shadingDevices.left || [],
      rightDevices: props.shadingDevices.right || [],
      topDevices: props.shadingDevices.top || [],
      frontDevices: props.shadingDevices.front || [],
      // Cache for storing values when switching modes
      windowRectCache: { width: initialWindowWidth, height: initialWindowHeight },
      windowPolygonCache: null,
      // Cache for shading devices mode switching
      deviceModeCache: {},
    };

    this.creationCount = [
      ...this.state.leftDevices, ...this.state.rightDevices, ...this.state.topDevices, ...this.state.frontDevices
    ]
      .map(dev => parseInt(dev.name.split(' ')[1], 10))
      .reduce((maxId, id) => Math.max(maxId, id), 0);
  }

  componentDidMount() {
    this.loadFromLocalStorage();
    this.notifyDataChanged();
    if (this.props.onReady) {
      this.props.onReady(this);
    }
  }

  addDevice(deviceType, stateKey) {
    this.creationCount++;
    const addedDev = {
      ...SCControlPanel.getConfigForDevice(deviceType, this.state.windowWidth, this.state.windowHeight),
      name: `Device ${this.creationCount}`,
    };

    this.setState(
      {
        [stateKey]: [addedDev, ...this.state[stateKey]],
      },
      () => this.notifyDataChanged(),
    );
  }

  removeDevice(stateKey, removeIndex) {
    this.setState(
      {
        [stateKey]: this.state[stateKey].filter((item, index) => index !== removeIndex)
      },
      () => this.notifyDataChanged(),
    );
  }

  showHideDevice(stateKey, targetIndex, isShow) {
    this.setState(
      {
        [stateKey]: this.state[stateKey].map((item, index) => index === targetIndex ? ({ ...item, isShow }) : item)
      },
      () => this.notifyDataChanged(),
    );
  }

  changeDevice(stateKey, name, data) {
    const newList = this.state[stateKey].map(
      item => item.name === name ? ({ ...item, ...SCControlPanel.convertToDevConfig(data) }) : item
    );
    this.setState(
      {
        [stateKey]: newList,
      },
      () => this.notifyDataChanged(),
    );
  }

  focusDevice(name) {
    const updatedDevices = Object.keys(this.state)
      .filter(key => key.toLowerCase().indexOf('devices') !== -1)
      .reduce((acc, deviceType) => {
        const newList = this.state[deviceType].map(device => ({ ...device, isHighLight: device.name === name}));
        return { ...acc, [deviceType]: newList };
      }, {});

    this.setState(updatedDevices, () => this.notifyDataChanged());
  }

  changeWindow({ width, height }) {
    this.setState(
      {
        windowWidth: width,
        windowHeight: height,
        windowMode: 'rectangle',
        windowRectCache: { width, height },
      },
      () => {
        this.saveToLocalStorage();
        this.notifyDataChanged();
      },
    );
  }

  changeWindowPolygon(name, data) {
    // data.points is required
    const points = (data && data.points) || [];
    const bboxW = points.reduce((max, p) => Math.max(max, parseFloat(p.x || 0)), 0) || 1;
    const bboxH = points.reduce((max, p) => Math.max(max, parseFloat(p.y || 0)), 0) || 1;
    this.setState(
      {
        windowMode: 'polygon',
        windowVertices: points,
        windowWidth: bboxW,
        windowHeight: bboxH,
        windowPolygonCache: points,
      },
      () => {
        this.saveToLocalStorage();
        this.notifyDataChanged();
      },
    );
  }

  saveToLocalStorage() {
    const cache = {
      windowRectCache: this.state.windowRectCache,
      windowPolygonCache: this.state.windowPolygonCache,
      deviceModeCache: this.state.deviceModeCache,
    };
    SCControlPanel.setStorageCache(cache);
  }

  loadFromLocalStorage() {
    const cache = SCControlPanel.getStorageCache();
    if (cache && Object.keys(cache).length > 0) {
      this.setState({
        windowRectCache: cache.windowRectCache || this.state.windowRectCache,
        windowPolygonCache: cache.windowPolygonCache || this.state.windowPolygonCache,
        deviceModeCache: cache.deviceModeCache || {},
      });
    }
  }

  componentWillReceiveProps(nextProps) {
    const updates = {};
    if (typeof nextProps.windowMode === 'string' && nextProps.windowMode !== this.state.windowMode) {
      updates.windowMode = nextProps.windowMode;
    }
    if (nextProps.window && nextProps.window.vertices && nextProps.window.vertices !== this.state.windowVertices) {
      const points = nextProps.window.vertices;
      updates.windowVertices = points;
      updates.windowWidth = points.reduce((max, p) => Math.max(max, parseFloat(p.x || 0)), 0) || 1;
      updates.windowHeight = points.reduce((max, p) => Math.max(max, parseFloat(p.y || 0)), 0) || 1;
    }
    if (Object.keys(updates).length) {
      this.setState(updates);
    }
  }

  notifyDataChanged() {
    const { onDataChanged } = this.props;
    if (onDataChanged) {
      const deviceKeys = Object.keys(this.state).filter(key => key.toLowerCase().indexOf('devices') !== -1);
      const devicesOnly = deviceKeys.reduce((acc, key) => {
        const list = (this.state[key] || []).filter(device => device.isShow);
        return { ...acc, [key]: list };
      }, {});
      const out = {
        ...devicesOnly,
        windowWidth: this.state.windowWidth,
        windowHeight: this.state.windowHeight,
        windowMode: this.state.windowMode,
        windowVertices: this.state.windowVertices,
      };
      onDataChanged(out);
    }
  }

  getCurrentData() {
    const deviceKeys = Object.keys(this.state).filter(key => key.toLowerCase().indexOf('devices') !== -1);
    const devicesOnly = deviceKeys.reduce((acc, key) => {
      const list = (this.state[key] || []).filter(device => device.isShow);
      return { ...acc, [key]: list };
    }, {});
    return {
      ...devicesOnly,
      windowWidth: this.state.windowWidth,
      windowHeight: this.state.windowHeight,
      windowMode: this.state.windowMode,
      windowVertices: this.state.windowVertices,
    };
  }

  renderWindow() {
    const { t } = this.props;
    const { windowMode, windowVertices, windowWidth, windowHeight, windowRectCache, windowPolygonCache, windowCircleRadius } = this.state;
    
    const switchToRectangle = () => {
      // Save current polygon state before switching
      if (windowMode === 'polygon' && windowVertices.length >= 3) {
        this.setState({ windowPolygonCache: windowVertices });
      }
      // Restore rectangle cache
      const cached = windowRectCache || { width: windowWidth || 1, height: windowHeight || 1 };
      this.setState({
        windowMode: 'rectangle',
        windowWidth: cached.width,
        windowHeight: cached.height,
      }, () => {
        this.saveToLocalStorage();
        this.notifyDataChanged();
      });
    };

    const switchToPolygon = () => {
      // Save current rectangle state before switching
      if (windowMode === 'rectangle') {
        this.setState({ windowRectCache: { width: windowWidth, height: windowHeight } });
      }
      // Restore polygon cache or create default
      const w = windowWidth || 1;
      const h = windowHeight || 1;
      const cachedVertices = windowPolygonCache && windowPolygonCache.length >= 3
        ? windowPolygonCache
        : [{ x: 0, y: 0 }, { x: w, y: 0 }, { x: w, y: h }, { x: 0, y: h }];
      const bboxW = cachedVertices.reduce((max, p) => Math.max(max, parseFloat(p.x || 0)), 0) || 1;
      const bboxH = cachedVertices.reduce((max, p) => Math.max(max, parseFloat(p.y || 0)), 0) || 1;
      this.setState({
        windowMode: 'polygon',
        windowVertices: cachedVertices,
        windowWidth: bboxW,
        windowHeight: bboxH,
      }, () => {
        this.saveToLocalStorage();
        this.notifyDataChanged();
      });
    };

    const switchToCircle = () => {
      // Save current rect/polygon state before switching
      if (windowMode === 'rectangle') {
        this.setState({ windowRectCache: { width: windowWidth, height: windowHeight } });
      }
      if (windowMode === 'polygon' && windowVertices.length >= 3) {
        this.setState({ windowPolygonCache: windowVertices });
      }
      const radius = windowCircleRadius || 0.5;
      const verts = generateCircleVertices(radius, 16, radius, radius); // keep all coords positive
      const minX = Math.min(...verts.map(p => parseFloat(p.x || 0)));
      const maxX = Math.max(...verts.map(p => parseFloat(p.x || 0)));
      const minY = Math.min(...verts.map(p => parseFloat(p.y || 0)));
      const maxY = Math.max(...verts.map(p => parseFloat(p.y || 0)));
      const bboxW = maxX - minX || radius * 2;
      const bboxH = maxY - minY || radius * 2;
      this.setState({
        windowMode: 'circle',
        windowVertices: verts,
        windowWidth: bboxW,
        windowHeight: bboxH,
      }, () => {
        this.saveToLocalStorage();
        this.notifyDataChanged();
      });
    };

    return (
      <InputFormCard title={'window'} t={t}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <Button
            bsStyle={windowMode === 'rectangle' ? 'primary' : 'default'}
            onClick={switchToRectangle}
          >
            {t('rectangle') || 'Rectangle'}
          </Button>
          <Button
            bsStyle={windowMode === 'polygon' ? 'primary' : 'default'}
            onClick={switchToPolygon}
          >
            {t('polygon') || 'Polygon'}
          </Button>
          <Button
            bsStyle={windowMode === 'circle' ? 'primary' : 'default'}
            onClick={switchToCircle}
          >
            {t('circle') || 'Circle'}
          </Button>
        </div>
        {windowMode === 'polygon' ? (
          <PolygonInputForm
            name="window"
            points={windowVertices}
            onDataChanged={(name, data) => this.changeWindowPolygon(name, data)}
            onFocusPoint={this.props.onFocusWindowVertex || null}
            axisLabels={{ x: 'Y', y: 'Z' }}
            t={t}
          />
        ) : windowMode === 'circle' ? (
          <div style={{ marginBottom: 12 }}>
            <h5 style={{ marginBottom: 8 }}>{t('radius') || 'Radius'}</h5>
            <input
              type="number"
              min={0.01}
              value={windowCircleRadius}
              onChange={e => {
                const radius = parseFloat(e.target.value) || 0;
                const verts = generateCircleVertices(radius, 16, radius, radius); // keep all coords positive
                const minX = Math.min(...verts.map(p => parseFloat(p.x || 0)));
                const maxX = Math.max(...verts.map(p => parseFloat(p.x || 0)));
                const minY = Math.min(...verts.map(p => parseFloat(p.y || 0)));
                const maxY = Math.max(...verts.map(p => parseFloat(p.y || 0)));
                const bboxW = maxX - minX || radius * 2;
                const bboxH = maxY - minY || radius * 2;
                this.setState({
                  windowCircleRadius: radius,
                  windowVertices: verts,
                  windowWidth: bboxW,
                  windowHeight: bboxH,
                }, () => {
                  this.saveToLocalStorage();
                  this.notifyDataChanged();
                });
              }}
              style={{ width: '100%' }}
            />
            <div style={{ color: '#777', marginTop: 4 }}>
              {t('circle auto generates 16 points') || 'Circle auto generates 16 points'}
            </div>
          </div>
        ) : (
          <RectangularInputForm
            name="window"
            size={{ width: windowWidth, height: windowHeight }}
            onDataChanged={(name, data) => this.changeWindow(data)}
            t={t}
          />
        )}
      </InputFormCard>
    );
  }

  switchDeviceToRectangle(stateKey, index, dev) {
    const cacheKey = `${stateKey}_${dev.name}`;
    const { deviceModeCache } = this.state;
    
    // Save current polygon/circle state
    if (dev.shape === 'polygon' && dev.vertices) {
      deviceModeCache[`${cacheKey}_polygon`] = dev.vertices;
    }
    if (dev.shape === 'circle') {
      deviceModeCache[`${cacheKey}_circle`] = { radius: dev.radius, vertices: dev.vertices };
    }
    
    // Restore rectangle cache
    const cachedRect = deviceModeCache[`${cacheKey}_rect`];
    const next = {
      ...dev,
      shape: 'rectangle',
      vertices: undefined,
      ...(cachedRect ? { origin: cachedRect.origin, size: cachedRect.size, xAngle: cachedRect.xAngle } : {}),
    };
    
    const list = this.state[stateKey].slice();
    list[index] = next;
    this.setState({ [stateKey]: list, deviceModeCache }, () => {
      this.saveToLocalStorage();
      this.notifyDataChanged();
    });
  }

  switchDeviceToPolygon(stateKey, index, dev) {
    const cacheKey = `${stateKey}_${dev.name}`;
    const { deviceModeCache, windowWidth, windowHeight } = this.state;
    
    // Get current device from state to ensure we have latest values
    const currentDev = this.state[stateKey][index];
    
    // Save current rectangle state
    if (currentDev.shape !== 'polygon') {
      deviceModeCache[`${cacheKey}_rect`] = {
        origin: currentDev.origin,
        size: currentDev.size,
        xAngle: currentDev.xAngle,
      };
    }
    
    // Restore polygon cache or create default based on rectangle size
    const cachedPolygon = deviceModeCache[`${cacheKey}_polygon`];
    let defaultVertices;
    if (cachedPolygon && cachedPolygon.length >= 3) {
      defaultVertices = cachedPolygon;
    } else {
      // Create polygon vertices from rectangle size (use device size or window size as fallback)
      const devSize = currentDev.size || {};
      const w = parseFloat(devSize.width) || parseFloat(windowWidth) || 1;
      const h = parseFloat(devSize.height) || parseFloat(windowHeight) || 1;
      // Use 2D vertices for shading devices
      defaultVertices = [
        { x: 0, y: 0 },
        { x: w, y: 0 },
        { x: w, y: h },
        { x: 0, y: h },
      ];
    }
    
    const next = { ...currentDev, shape: 'polygon', vertices: defaultVertices };
    const list = this.state[stateKey].slice();
    list[index] = next;
    this.setState({ [stateKey]: list, deviceModeCache }, () => {
      this.saveToLocalStorage();
      this.notifyDataChanged();
    });
  }

  switchDeviceToCircle(stateKey, index, dev) {
    const cacheKey = `${stateKey}_${dev.name}`;
    const { deviceModeCache, windowWidth, windowHeight } = this.state;
    const currentDev = this.state[stateKey][index];

    // Save rectangle state when leaving non-circle
    if (currentDev.shape !== 'circle') {
      deviceModeCache[`${cacheKey}_rect`] = {
        origin: currentDev.origin,
        size: currentDev.size,
        xAngle: currentDev.xAngle,
      };
    }

    // Restore cached circle or build default from size
    const cachedCircle = deviceModeCache[`${cacheKey}_circle`];
    let radius = cachedCircle && cachedCircle.radius ? cachedCircle.radius : null;
    let vertices = cachedCircle && cachedCircle.vertices ? cachedCircle.vertices : null;
    const origin = currentDev.origin || { x: 0, y: 0, z: 0 };

    if (!radius) {
      const devSize = currentDev.size || {};
      const w = parseFloat(devSize.width) || parseFloat(windowWidth) || 1;
      const h = parseFloat(devSize.height) || parseFloat(windowHeight) || 1;
      radius = Math.min(w, h) / 2;
    }
    if (!vertices || vertices.length < 3) {
      vertices = generateCircleVertices(radius, 16, 0, 0);
    }

    const next = { ...currentDev, shape: 'circle', radius, vertices, origin, xAngle: currentDev.xAngle || currentDev.angle };
    const list = this.state[stateKey].slice();
    list[index] = next;
    this.setState({ [stateKey]: list, deviceModeCache }, () => {
      this.saveToLocalStorage();
      this.notifyDataChanged();
    });
  }

  renderAzimuthField(t, stateKey, index, dev, deviceType) {
    const isEditable = deviceType === 'left' || deviceType === 'right';
    return (
      <div style={{ marginTop: 12 }}>
        <h5 style={{ marginBottom: 8 }}>{t('azimuth') || 'Azimuth'} (°)</h5>
        <input
          type="number"
          min={-360}
          max={360}
          disabled={!isEditable}
          value={dev.azimuth !== undefined && dev.azimuth !== null ? dev.azimuth : ''}
          placeholder={isEditable ? '' : '-'}
          onChange={e => {
            if (!isEditable) return;
            const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
            const list = this.state[stateKey].slice();
            list[index] = { ...dev, azimuth: val };
            this.setState({ [stateKey]: list }, () => {
              this.saveToLocalStorage();
              this.notifyDataChanged();
            });
          }}
          onFocus={() => isEditable && this.focusDevice(dev.name)}
          onBlur={() => isEditable && this.focusDevice(null)}
          style={{ width: '100%', background: isEditable ? undefined : '#f5f5f5', cursor: isEditable ? undefined : 'not-allowed' }}
        />
      </div>
    );
  }

  renderShadingDevices(deviceType, stateKey) {
    const { t } = this.props;
    const getProps = (dev, type) => {
      let props = {
        name: dev.name,
        origin: dev.origin,
        size: dev.size,
      };

      if (type === 'front' || type === 'top') {
        props = {
          ...props,
          angle: dev.xAngle,
        };
      }
      return props;
    };

    return (
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'sticky', top: 0, zIndex: 10, display: 'flex', justifyContent: 'flex-end', marginBottom: 12, paddingTop: 8, paddingBottom: 8, background: '#fff' }}>
          <Button
            bsStyle="primary"
            onClick={() => this.addDevice(deviceType, stateKey)}
          >
            <Glyphicon glyph="glyphicon glyphicon-plus" /> {t('add device') || 'Add Device'}
          </Button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {this.state[stateKey].map((dev, index) => (
            <InputFormCard
              title={dev.name}
              key={dev.name}
              onDelete={() => this.removeDevice(stateKey, index)}
              onShowHide={isShow => this.showHideDevice(stateKey, index, isShow)}
              t={t}
              fullWidth
            >
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <Button
                  bsStyle={dev.shape === 'rectangle' ? 'primary' : 'default'}
                  onClick={() => this.switchDeviceToRectangle(stateKey, index, dev)}
                >
                  {t('rectangle') || 'Rectangle'}
                </Button>
                <Button
                  bsStyle={dev.shape === 'polygon' ? 'primary' : 'default'}
                  onClick={() => this.switchDeviceToPolygon(stateKey, index, dev)}
                >
                  {t('polygon') || 'Polygon'}
                </Button>
                <Button
                  bsStyle={dev.shape === 'circle' ? 'primary' : 'default'}
                  onClick={() => this.switchDeviceToCircle(stateKey, index, dev)}
                >
                  {t('circle') || 'Circle'}
                </Button>
              </div>
              {dev.shape === 'polygon' ? (
                <div style={{ display: 'flex', gap: 24 }}>
                  <div style={{ flex: 1 }}>
                    <PolygonInputForm
                      name={dev.name}
                      points={dev.vertices || []}
                      origin={dev.origin}
                      use3DVertices={false}
                      axisLabels={
                        (deviceType === 'left' || deviceType === 'right')
                          ? { x: 'X', y: 'Z' }
                          : (deviceType === 'top' || deviceType === 'front')
                            ? { x: 'Y', y: 'X' }
                            : null
                      }
                      onDataChanged={(name, data) =>
                        this.changeDevice(stateKey, name, { vertices: data.points, origin: data.origin })
                      }
                      onFocusPoint={idx => {
                        this.focusDevice(idx !== null ? dev.name : null);
                        if (this.props.onFocusDeviceVertex) {
                          this.props.onFocusDeviceVertex(deviceType, dev.name, idx);
                        }
                      }}
                      t={t}
                    />
                    {(deviceType === 'top' || deviceType === 'front') && (
                      <div style={{ marginTop: 12 }}>
                        <h5 style={{ marginBottom: 8 }}>{t('angle') || 'Angle'}</h5>
                        <input
                          type="number"
                          min={0}
                          max={90}
                          value={dev.xAngle !== undefined ? dev.xAngle : (dev.angle || 0)}
                          onChange={e => {
                            const angleVal = parseFloat(e.target.value) || 0;
                            const list = this.state[stateKey].slice();
                            list[index] = { ...dev, xAngle: angleVal, angle: angleVal };
                            this.setState({ [stateKey]: list }, () => {
                              this.saveToLocalStorage();
                              this.notifyDataChanged();
                            });
                          }}
                          style={{ width: '100%' }}
                        />
                      </div>
                    )}
                    {this.renderAzimuthField(t, stateKey, index, dev, deviceType)}
                  </div>
                </div>
              ) : dev.shape === 'circle' ? (
                <div style={{ display: 'flex', gap: 24 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: 16 }}>
                      <h5 style={{ marginBottom: 8 }}>{t('position') || 'Position'}</h5>
                      <div style={{ display: 'flex', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                          <label>{`X (${t('meter abbrv')})`}</label>
                          <input
                            type="number"
                            value={(dev.origin && dev.origin.x) || 0}
                            onChange={e => {
                              const origin = { ...(dev.origin || {}), x: e.target.value };
                              const cacheKey = `${stateKey}_${dev.name}`;
                              const radius = dev.radius || 0;
                              const vertices = generateCircleVertices(radius, 16, 0, 0);
                              const deviceModeCache = { ...this.state.deviceModeCache, [`${cacheKey}_circle`]: { radius, vertices } };
                              const list = this.state[stateKey].slice();
                              list[index] = { ...dev, origin, vertices };
                              this.setState({ [stateKey]: list, deviceModeCache }, () => {
                                this.saveToLocalStorage();
                                this.notifyDataChanged();
                              });
                            }}
                            onFocus={() => this.focusDevice(dev.name)}
                            onBlur={() => this.focusDevice(null)}
                            style={{ width: '100%' }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label>{`Y (${t('meter abbrv')})`}</label>
                          <input
                            type="number"
                            value={(dev.origin && dev.origin.y) || 0}
                            onChange={e => {
                              const origin = { ...(dev.origin || {}), y: e.target.value };
                              const cacheKey = `${stateKey}_${dev.name}`;
                              const radius = dev.radius || 0;
                              const vertices = generateCircleVertices(radius, 16, 0, 0);
                              const deviceModeCache = { ...this.state.deviceModeCache, [`${cacheKey}_circle`]: { radius, vertices } };
                              const list = this.state[stateKey].slice();
                              list[index] = { ...dev, origin, vertices };
                              this.setState({ [stateKey]: list, deviceModeCache }, () => {
                                this.saveToLocalStorage();
                                this.notifyDataChanged();
                              });
                            }}
                            onFocus={() => this.focusDevice(dev.name)}
                            onBlur={() => this.focusDevice(null)}
                            style={{ width: '100%' }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label>{`Z (${t('meter abbrv')})`}</label>
                          <input
                            type="number"
                            value={(dev.origin && dev.origin.z) || 0}
                            onChange={e => {
                              const origin = { ...(dev.origin || {}), z: e.target.value };
                              const cacheKey = `${stateKey}_${dev.name}`;
                              const radius = dev.radius || 0;
                              const vertices = generateCircleVertices(radius, 16, 0, 0);
                              const deviceModeCache = { ...this.state.deviceModeCache, [`${cacheKey}_circle`]: { radius, vertices } };
                              const list = this.state[stateKey].slice();
                              list[index] = { ...dev, origin, vertices };
                              this.setState({ [stateKey]: list, deviceModeCache }, () => {
                                this.saveToLocalStorage();
                                this.notifyDataChanged();
                              });
                            }}
                            onFocus={() => this.focusDevice(dev.name)}
                            onBlur={() => this.focusDevice(null)}
                            style={{ width: '100%' }}
                          />
                        </div>
                      </div>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <h5 style={{ marginBottom: 8 }}>{t('radius') || 'Radius'}</h5>
                      <input
                        type="number"
                        min={0.01}
                        value={dev.radius || ''}
                        onChange={e => {
                          const radius = parseFloat(e.target.value) || 0;
                          const vertices = generateCircleVertices(radius, 16, 0, 0);
                          // cache circle state for toggling back
                          const cacheKey = `${stateKey}_${dev.name}`;
                          const deviceModeCache = { ...this.state.deviceModeCache, [`${cacheKey}_circle`]: { radius, vertices } };
                          const list = this.state[stateKey].slice();
                          list[index] = { ...dev, shape: 'circle', radius, vertices };
                          this.setState({ [stateKey]: list, deviceModeCache }, () => {
                            this.saveToLocalStorage();
                            this.notifyDataChanged();
                          });
                        }}
                        onFocus={() => this.focusDevice(dev.name)}
                        onBlur={() => this.focusDevice(null)}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div style={{ color: '#777' }}>
                      {t('circle auto generates 16 points') || 'Circle auto generates 16 points'}
                    </div>
                    {(deviceType === 'top' || deviceType === 'front') && (
                      <div style={{ marginTop: 12 }}>
                        <h5 style={{ marginBottom: 8 }}>{t('angle') || 'Angle'}</h5>
                        <input
                          type="number"
                          min={0}
                          max={90}
                          value={dev.xAngle !== undefined ? dev.xAngle : (dev.angle || 0)}
                          onChange={e => {
                            const angleVal = parseFloat(e.target.value) || 0;
                            const list = this.state[stateKey].slice();
                            list[index] = { ...dev, xAngle: angleVal, angle: angleVal };
                            this.setState({ [stateKey]: list }, () => {
                              this.saveToLocalStorage();
                              this.notifyDataChanged();
                            });
                          }}
                          onFocus={() => this.focusDevice(dev.name)}
                          onBlur={() => this.focusDevice(null)}
                          style={{ width: '100%' }}
                        />
                      </div>
                    )}
                    {this.renderAzimuthField(t, stateKey, index, dev, deviceType)}
                  </div>
                </div>
              ) : (
                <div>
                  <RectangularInputForm
                    {...getProps(dev, deviceType)}
                    onDataChanged={(name, data) => this.changeDevice(stateKey, name, data)}
                    onFocus={() => this.focusDevice(dev.name)}
                    onBlur={() => this.focusDevice(null)}
                    t={t}
                  />
                  {this.renderAzimuthField(t, stateKey, index, dev, deviceType)}
                </div>
              )}
            </InputFormCard>
          ))}
        </div>
      </div>
    );
  }

  render() {
    const { t } = this.props;
    return (
      <Tabs id="sc-control-panel">
        <Tab eventKey={1} title={t('window')}>
          {this.renderWindow()}
        </Tab>
        <Tab eventKey={2} title={t('left shading device')}>
          {this.renderShadingDevices('left', 'leftDevices')}
        </Tab>
        <Tab eventKey={3} title={t('right shading device')}>
          {this.renderShadingDevices('right', 'rightDevices')}
        </Tab>
        <Tab eventKey={4} title={t('top shading device')}>
          {this.renderShadingDevices('top', 'topDevices')}
        </Tab>
        <Tab eventKey={5} title={t('front shading device')}>
          {this.renderShadingDevices('front', 'frontDevices')}
        </Tab>
      </Tabs>
    );
  }
}

SCControlPanel.defaultProps = {
  window: {},
  shadingDevices: {},
  onDataChanged: null,
};

export default translate('translations')(SCControlPanel);



// WEBPACK FOOTER //
// ./src/components/sc_editor/SCControlPanel.js